import { assertNotEmpty } from '@festina-life/core'
import { useMemo } from 'react'

import {
  ConfigurationFieldType,
  type ConfigurationSimpleValueField,
  type ConfigurationSimpleValueFieldValue,
  type FPlusExpressionValue,
  isNotRelevantField,
  isSimpleValueSetupField,
  type SimpleValueSetupField,
  type SimpleValueSetupFieldValue,
} from '../../types/templateModel'
import {
  generateEmptyAttributeExpressionValue,
  generateEmptyFieldValue,
} from '../../uiModels/createEmptyFieldHelpers'
import { useFieldLiteralExpressionInformation } from '../fplusExpression/useFieldLiteralExpressionInformation'
import { TemplateFieldExpression } from '../templateFieldExpression/TemplateFieldExpression'

type Props = {
  field: SimpleValueSetupField | ConfigurationSimpleValueField
  isExpanded?: boolean
}

const fPlusExpressionCodeFromField = (
  field: SimpleValueSetupField | ConfigurationSimpleValueField,
) => {
  let fPlusValue
  let updateFn
  let path
  let valueOrigin

  if (isSimpleValueSetupField(field)) {
    fPlusValue = field.value?.defaultValue
    path = `${field.path}.defaultValue`
    valueOrigin = field.value?.defaultValue?.origin

    const fieldValue = (field.value ??
      generateEmptyFieldValue({
        field,
      })) as SimpleValueSetupFieldValue

    updateFn = (nextValue: FPlusExpressionValue) => {
      const value =
        fieldValue.defaultValue ??
        generateEmptyAttributeExpressionValue({
          path: fieldValue.path,
          fieldName: 'defaultValue',
        })
      delete value.expression
      return field.update(
        nextValue.fplusSource
          ? {
              ...fieldValue,
              defaultValue: {
                ...value,
                expression: nextValue,
                origin: value.path,
              },
            }
          : null,
      )
    }
  } else if (
    field.fieldType === ConfigurationFieldType.ConfigurationExpressionValue
  ) {
    const fieldValue = (field.value ??
      generateEmptyFieldValue({
        field,
      })) as ConfigurationSimpleValueFieldValue

    path = fieldValue.path
    valueOrigin = field.value?.origin
    fPlusValue = fieldValue

    updateFn = (nextValue: FPlusExpressionValue) => {
      delete fieldValue.expression
      return field.update(
        nextValue.fplusSource
          ? {
              ...fieldValue,
              expression: nextValue,
              origin: fieldValue.path,
            }
          : null,
      )
    }
  }

  assertNotEmpty(path)

  return {
    value: fPlusValue,
    path,
    valueOrigin,
    update: updateFn
      ? updateFn
      : () => {
          throw new Error('Unsupported field type for updates')
        },
  }
}

export const SimpleFieldValue = ({ field, isExpanded }: Props) => {
  const literalExpressionInformation =
    useFieldLiteralExpressionInformation(field)
  const fPlusExpressionCode = useMemo(
    () => fPlusExpressionCodeFromField(field),
    [field],
  )

  const expression = fPlusExpressionCode.value?.expression

  return (
    <TemplateFieldExpression
      expression={expression}
      isExpanded={!!isExpanded}
      onChange={
        isNotRelevantField(field) ? undefined : fPlusExpressionCode.update
      }
      path={fPlusExpressionCode.path}
      fallbackCode=""
      typeInformation={literalExpressionInformation}
      fromOrigin={fPlusExpressionCode.valueOrigin}
    />
  )
}
